import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { Invoice, InvoiceStatus, Bid, BidStatus } from '../../types/models';

const SellerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [recentBids, setRecentBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch seller's invoices
        const invoicesData = await apiService.get<Invoice[]>('/invoices');
        setInvoices(invoicesData);

        // Fetch recent bids on seller's invoices
        const bidsData: Bid[] = [];
        for (const invoice of invoicesData) {
          if (invoice.status === InvoiceStatus.VERIFIED) {
            const bidResponse = await apiService.get<Bid[]>(`/bids?invoice_id=${invoice.id}`);
            bidsData.push(...bidResponse);
          }
        }
        setRecentBids(bidsData.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ).slice(0, 5)); // Get 5 most recent bids
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail 
          ? typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail)
          : 'Error al cargar los datos del panel';
        
        setError(errorMessage);
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status: InvoiceStatus | string) => {
    switch (status) {
      case InvoiceStatus.PENDING:
      case 'pending':
        return 'warning';
      case InvoiceStatus.VERIFIED:
      case 'verified':
        return 'success';
      case InvoiceStatus.SOLD:
      case 'sold':
        return 'info';
      case InvoiceStatus.EXPIRED:
      case 'expired':
        return 'error';
      case InvoiceStatus.REJECTED:
      case 'rejected':
        return 'error';
      case InvoiceStatus.DELISTED:
      case 'delisted':
        return 'default';
      default:
        return 'default';
    }
  };

  const getBidStatusColor = (status: BidStatus) => {
    switch (status) {
      case BidStatus.ACTIVE:
        return 'info';
      case BidStatus.ACCEPTED:
        return 'success';
      case BidStatus.REJECTED:
        return 'error';
      case BidStatus.EXPIRED:
        return 'warning';
      default:
        return 'default';
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">Panel del Vendedor</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/invoices/upload')}
        >
          Subir Factura
        </Button>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total de Facturas</Typography>
              <Typography variant="h3">{invoices.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Facturas Verificadas</Typography>
              <Typography variant="h3">
                {invoices.filter(i => i.status === InvoiceStatus.VERIFIED).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Facturas Vendidas</Typography>
              <Typography variant="h3">
                {invoices.filter(i => i.status === InvoiceStatus.SOLD).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Facturas Recientes</Typography>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Actualizar
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Factura #</TableCell>
                <TableCell>Fecha de Emisión</TableCell>
                <TableCell>Fecha de Vencimiento</TableCell>
                <TableCell>Monto Neto</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" py={2}>
                      No se encontraron facturas. Sube tu primera factura para comenzar.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .slice(0, 5)
                  .map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.invoice_number}</TableCell>
                      <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell>{formatCurrency(invoice.net_amount, invoice.currency)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={invoice.status} 
                          color={getStatusColor(invoice.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => navigate(`/invoices/${invoice.id}`)}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button onClick={() => navigate('/invoices')}>Ver Todas las Facturas</Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h5" mb={2}>Ofertas Recientes</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Factura #</TableCell>
                <TableCell>Monto de Oferta</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentBids.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" py={2}>
                      No se han recibido ofertas aún. Las facturas verificadas aparecerán en el mercado para los compradores.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentBids.map((bid) => (
                  <TableRow key={bid.id}>
                    <TableCell>{bid.invoice_id}</TableCell>
                    <TableCell>{formatCurrency(bid.amount)}</TableCell>
                    <TableCell>{formatDate(bid.created_at)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={bid.status} 
                        color={getBidStatusColor(bid.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => navigate(`/bids/${bid.id}`)}
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SellerDashboard;
